// Copyright (c) StrangeBee 2024

import React, { ReactElement, ReactNode, useState } from 'react';
import { useLocalMessage } from '@Helpers/';
import { DashboardOutput, DashboardInput, UserOutput, AdminPermissionsEnum } from 'thehive-sdk';
import { FormInstance } from 'antd/lib/form';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { IconLabel } from '@Atoms/Info';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import useUserPermissions from '@Hooks/useUserPermissions';
import { Form } from 'antd';

export interface DashboardFormValues {
    title: string;
    description: string;
    status: 'Private' | 'Shared';
    owner: string;
}

interface DashboardFormProps {
    onFinish?(values: DashboardFormValues): void;
    initialValues?: DashboardOutput | DashboardInput;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    children?: ReactNode;
    isUserOwner?: boolean;
    isEditing?: boolean;
    onCancel: () => void;
    submitTouchedValues?: boolean;
    ownerList?: UserOutput[];
    allDashboardList?: DashboardOutput[];
}

const DashboardForm = ({
    onFinish,
    initialValues,
    form,
    isValid,
    onChange,
    children,
    isUserOwner = true,
    isEditing = false,
    onCancel,
    submitTouchedValues,
    ownerList,
    allDashboardList = [],
}: DashboardFormProps): ReactElement => {
    const i = useLocalMessage();

    const manageUserPermission = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_USER] });

    const status = Form.useWatch(['status'], form);

    const showUserSelectPermission = (changedStatusValues: string) => {
        if (!changedStatusValues && initialValues && initialValues.status === 'Shared' && manageUserPermission) {
            return true;
        }

        return !!(
            isEditing &&
            changedStatusValues &&
            manageUserPermission &&
            (changedStatusValues === 'Shared' ||
                (initialValues && initialValues.status === 'Shared' && changedStatusValues !== 'Private'))
        );
    };

    const [showUserSelect, setShowUserSelect] = useState<boolean>(showUserSelectPermission(status));

    const groupList = allDashboardList?.reduce(
        (acc, { group }) => (group && !acc.includes(group) ? [...acc, group] : acc),
        [] as string[],
    );

    const handleFormValuesChange = (changedValues: { status: string }) => {
        if (changedValues.status) {
            setShowUserSelect(showUserSelectPermission(changedValues.status));
        }
    };

    return (
        <THForm<DashboardFormValues>
            name="create-dashboard-form"
            onFinish={onFinish}
            initialValues={{
                status: 'Private',
                ...initialValues,
            }}
            onValuesChange={(changedValues) => {
                onChange();
                handleFormValuesChange(changedValues);
            }}
            form={form}
            submitTouchedValues={submitTouchedValues}
        >
            <THForm.Layout footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} />}>
                <THForm.Item
                    name="group"
                    label={i('dashboard.create.form.group.label')}
                    rules={[{ required: true, message: i('dashboard.create.form.required') }]}
                >
                    <THAutoComplete
                        options={groupList?.map((group) => ({ label: group, value: group }))}
                        placeholder={i('dashboard.create.form.group.placeholder')}
                        filtered
                    />
                </THForm.Item>

                <THForm.Item
                    label={i('dashboard.create.form.title')}
                    name="title"
                    rules={[{ required: true, message: i('dashboard.create.form.required') }]}
                >
                    <THInput placeholder={i('dashboard.create.form.title-placeholder')} />
                </THForm.Item>

                <THForm.Item
                    label={i('dashboard.create.form.description')}
                    name="description"
                    rules={[{ required: true, message: i('dashboard.create.form.required') }]}
                >
                    <THTextArea placeholder={i('dashboard.create.form.description-placeholder')} />
                </THForm.Item>

                <THForm.Item
                    label={i('dashboard.create.form.visibility')}
                    name="status"
                    rules={[{ required: true, message: i('dashboard.create.form.required') }]}
                >
                    <THRadio.Group disabled={!isUserOwner}>
                        <THRadio value="Private">{i('dashboard.create.form.private')}</THRadio>
                        <THRadio value="Shared">{i('dashboard.create.form.shared')}</THRadio>
                    </THRadio.Group>
                </THForm.Item>

                {showUserSelect && ownerList && (
                    <THForm.Item
                        className="bulk-edit-form-field"
                        name="owner"
                        label={i('dashboard.edit.form.owner.label')}
                    >
                        <THSelect allowClear borderless placeholder={i('dashboard.edit.form.owner.placeholder')}>
                            {ownerList?.map(({ _id, name, avatar, login }) => (
                                <THSelect.Option key={_id} value={login}>
                                    <IconLabel
                                        key={_id}
                                        icon={<UserAvatar name={name} avatar={avatar} />}
                                        label={name}
                                    />
                                </THSelect.Option>
                            ))}
                        </THSelect>
                    </THForm.Item>
                )}

                <THForm.Item name="definition" hidden>
                    <THInput />
                </THForm.Item>

                {children}
            </THForm.Layout>
        </THForm>
    );
};

export default DashboardForm;
